import { useEffect, useState, useCallback } from "react";
import { BlogPost } from "../Types/Types";

const apiUrl = process.env.REACT_APP_API_URL;
const baseUrl = `${apiUrl}/api/blog`;

const useBlog = () => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    const lang = localStorage.getItem("lang") || "en";
    try {
      const res = await fetch(
        `${baseUrl}?lang=${lang}&search=${encodeURIComponent(searchQuery)}&limit=100`
      );
      if (!res.ok) throw new Error("Failed to fetch blog posts");
      const data = await res.json();
      setBlogs(data.blogs || []);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  const createBlog = async (formData: FormData): Promise<boolean> => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(baseUrl, {
        method: "POST",
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        body: formData,
      });
      if (!res.ok) throw new Error("Failed to create blog post");
      await fetchBlogs();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const updateBlog = async (id: string, formData: FormData): Promise<boolean> => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${baseUrl}/${id}`, {
        method: "PUT",
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        body: formData,
      });
      if (!res.ok) throw new Error("Failed to update blog post");
      await fetchBlogs();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteBlog = async (id: string): Promise<boolean> => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${baseUrl}/${id}`, {
        method: "DELETE",
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      if (!res.ok) throw new Error("Failed to delete blog post");
      setBlogs((prev) => prev.filter((b) => b._id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  return {
    blogs,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    fetchBlogs,
    createBlog,
    updateBlog,
    deleteBlog,
  };
};

export default useBlog;
